import React, { useState, useMemo } from "react";
import { Search, Heart, Share2, Eye, X } from "lucide-react";
import { MOCK_TUTORIALS, MOCK_TOPICS } from "../data/mockTutorials";
import "../styles/tutorials.css";

export default function Tutorials() {
  const [activeTopic, setActiveTopic] = useState("All");
  const [activeVideo, setActiveVideo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("All levels");
  const [selectedRelevance, setSelectedRelevance] = useState("Relevance");

  const filteredTutorials = useMemo(() => {
    return MOCK_TUTORIALS.filter((tut) => {
      if (activeTopic !== "All") {
        const matchTopic = tut.topic?.toLowerCase() === activeTopic.toLowerCase() || tut.tags.some(t => t.toLowerCase().includes(activeTopic.toLowerCase()));
        if (!matchTopic) return false;
      }
      
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchSearch = tut.title.toLowerCase().includes(q) || tut.tags.some(t => t.toLowerCase().includes(q));
        if (!matchSearch) return false;
      }
      
      if (selectedLevel !== "All levels") {
        if (tut.level.toLowerCase() !== selectedLevel.toLowerCase()) return false;
      }
      
      return true;
    }).sort((a, b) => {
      if (selectedRelevance === "Most Viewed") {
        return parseInt(b.views || 0) - parseInt(a.views || 0);
      }
      if (selectedRelevance === "Most Recent") {
        return b.id - a.id;
      }
      return 0;
    });
  }, [activeTopic, searchQuery, selectedLevel, selectedRelevance]);

  const getEmbedUrl = (url) => {
    if (!url) return "";
    const videoIdMatch = url.match(/v=([^&]+)/);
    if (videoIdMatch && videoIdMatch[1]) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}?autoplay=1`;
    }
    return "";
  };

  return (
    <div className="tutorials-page">
      {/* HEADER */}
      <div className="tutorials-header">
        <div className="tutorials-header-left">
          <div className="tutorials-icon-wrap">
            🧑‍🏫
          </div>
          <div className="tutorials-title-box">
            <h1>AI Video Tutorials</h1>
            <p>Learn how to master the best AI tools on video</p>
          </div>
        </div>
        

      </div>

      {/* FILTERS */}
      <div className="tutorials-filters-wrapper">
        <div className="filter-controls">
          <div className="search-input-wrapper">
            <Search size={18} color="#94a3b8" />
            <input 
              type="text" 
              placeholder="Search..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <select 
            className="dropdown-select"
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
          >
            <option>All levels</option>
            <option>Beginner</option>
            <option>Intermediate</option>
            <option>Advanced</option>
          </select>
          
          <select 
            className="dropdown-select"
            value={selectedRelevance}
            onChange={(e) => setSelectedRelevance(e.target.value)}
          >
            <option>Relevance</option>
            <option>Most Recent</option>
            <option>Most Viewed</option>
          </select>
          
          <button className="favorites-btn">
            <Heart size={16} color="#64748b" /> Favorites
          </button>
        </div>

        {/* TOPICS */}
        <div className="topics-row">
          {MOCK_TOPICS.map((topic, i) => (
            <button 
              key={i} 
              className={`topic-pill ${activeTopic === topic ? 'active' : ''}`}
              onClick={() => setActiveTopic(topic)}
            >
              {topic}
            </button>
          ))}
        </div>
      </div>

      {/* GRID */}
      <div className="tutorials-grid">
        {filteredTutorials.map((tut) => (
          <div className="tutorial-card" key={tut.id}>
            <div 
              style={{ cursor: 'pointer' }}
              onClick={() => setActiveVideo(tut)}
            >
              <div className="tutorial-thumbnail-container">
                <img src={tut.thumbnail} alt={tut.title} className="tutorial-thumbnail" />
                <div className="tutorial-duration">{tut.duration}</div>
              </div>
            </div>
            
            <div className="tutorial-card-body">
              <div>
                <span className={`level-badge ${tut.level.toLowerCase()}`}>
                  {tut.level}
                </span>
              </div>
              <div 
                style={{ cursor: 'pointer' }}
                onClick={() => setActiveVideo(tut)}
              >
                <h3 className="tutorial-title">{tut.title}</h3>
              </div>
              <div className="tutorial-tags">
                {tut.tags.map((tag, idx) => (
                  <span key={idx}>{tag}</span>
                ))}
              </div>
            </div>

            <div className="tutorial-card-footer">
              <div className="tutorial-footer-actions">
                <button className="tutorial-footer-btn"><Heart size={14} strokeWidth={1.5} /></button>
                <button className="tutorial-footer-btn"><Share2 size={14} strokeWidth={1.5} /></button>
              </div>
              <div className="tutorial-views">
                <Eye size={14} strokeWidth={1.5} /> {tut.views}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* VIDEO MODAL */}
      {activeVideo && (
        <div className="video-modal-overlay" onClick={() => setActiveVideo(null)}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <div className="video-modal-title">
                <h2>{activeVideo.title}</h2>
              </div>
              <button className="video-modal-close" onClick={() => setActiveVideo(null)}>
                <X size={24} />
              </button>
            </div>
            <div className="video-modal-body">
              <iframe
                src={getEmbedUrl(activeVideo.url)}
                title={activeVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
